import React from 'react';
import {
    Grid,
    Typography,
    Box,
    Collapse,
    Alert,
    Paper
} from '@mui/material';
import { DmsSwitch } from 'dmsbasereact';

/**
 * SettingsSection - Reusable component for settings sections.
 * Uses a clean card layout without colored header for less visual noise.
 *
 * @param {string} title - Section title
 * @param {string} description - Section description
 * @param {string} switchName - Name attribute for the enable/disable switch
 * @param {string} switchLabel - Label for the switch
 * @param {boolean} enabled - Current enabled state
 * @param {function} onEnabledChange - Callback when enabled state changes (receives boolean)
 * @param {string} disabledWarning - Warning message to show when disabled
 * @param {React.ReactNode} children - Child components (form fields)
 * @param {boolean} showSwitch - Whether to show the enable/disable switch (default: true)
 */
export const SettingsSection = ({
    title,
    description,
    switchName,
    switchLabel = 'Enabled',
    enabled = true,
    onEnabledChange,
    disabledWarning,
    children,
    showSwitch = true
}) => {
    // DmsSwitch onChange returns (name, value) where value is "true"/"false" string
    const handleSwitchChange = (name, value) => {
        const boolValue = value === 'true' || value === true;
        onEnabledChange?.(boolValue);
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                {title}
            </Typography>

            {description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {description}
                </Typography>
            )}

            {showSwitch && (
                <Box sx={{ mb: 2 }}>
                    <DmsSwitch
                        name={switchName}
                        label={switchLabel}
                        checked={enabled}
                        onChange={handleSwitchChange}
                    />
                </Box>
            )}

            <Collapse in={enabled}>
                <Grid container spacing={2}>
                    {children}
                </Grid>
            </Collapse>

            {!enabled && disabledWarning && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {disabledWarning}
                </Alert>
            )}
        </Paper>
    );
};

/**
 * SettingsField - Wrapper for individual form fields within a SettingsSection.
 * Provides consistent grid sizing.
 *
 * @param {number} xs - Grid xs size (default: 12)
 * @param {number} sm - Grid sm size (default: 6)
 * @param {number} md - Grid md size (default: 4)
 * @param {React.ReactNode} children - The form field component
 */
export const SettingsField = ({ xs = 12, sm = 6, md = 4, children }) => (
    <Grid item xs={xs} sm={sm} md={md}>
        {children}
    </Grid>
);

/**
 * SettingsRow - Full-width row within a SettingsSection.
 *
 * @param {React.ReactNode} children - Content for the row
 */
export const SettingsRow = ({ children }) => (
    <Grid item xs={12}>
        {children}
    </Grid>
);
